import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CustomKeypad } from "@/pages/sy/components/CustomKeypad";
import { WeightInputForOperation } from "../components/WeightInputForOperation";
import {
  normalizeCraneMagnitudeInput,
  parseCraneMagnitude,
} from "../utils/formatCraneMagnitude";

interface RequestedHeatWeightModalProps {
  open: boolean;
  requestedHeatWeight: number | null;
  recipeTotalWeight: number;
  scheduledHeatName: string | null;
  isSaving: boolean;
  title?: string;
  inputLabel?: string;
  onCancel: () => void;
  onSave: (requestedHeatWeight: number | null) => void;
}

const QUICK_ADJUSTMENTS = [
  { value: 15000, color: "#285b34" },
  { value: -15000, color: "#91461f" },
  { value: 10000, color: "#356f40" },
  { value: -10000, color: "#a65424" },
  { value: 5000, color: "#4f8758" },
  { value: -5000, color: "#bd7138" },
  { value: 1000, color: "#719d77" },
  { value: -1000, color: "#ce8e59" },
] as const;
const MAX_INTEGER_DIGITS = 10;

export const RequestedHeatWeightModal = ({
  open,
  requestedHeatWeight,
  recipeTotalWeight,
  scheduledHeatName,
  isSaving,
  title = "Requested Heat Weight",
  inputLabel = "",
  onCancel,
  onSave,
}: RequestedHeatWeightModalProps) => {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!open) return;

    const initialValue = requestedHeatWeight ?? recipeTotalWeight;
    setInputValue(initialValue > 0 ? Math.round(initialValue).toString() : "");
  }, [open, recipeTotalWeight, requestedHeatWeight]);

  const numericValue = useMemo(() => {
    if (inputValue.trim() === "") return null;

    return parseCraneMagnitude(inputValue);
  }, [inputValue]);

  const isValid = inputValue === "" || (numericValue ?? 0) > 0;

  const handleInputChange = (newValue: string) => {
    const normalizedValue = normalizeCraneMagnitudeInput(newValue);
    if (normalizedValue.length > MAX_INTEGER_DIGITS) return;

    setInputValue(normalizedValue);
  };

  const handleAdjustment = (adjustment: number) => {
    const currentValue = numericValue ?? 0;
    const adjustedValue = Math.max(0, currentValue + adjustment);
    setInputValue(Math.round(adjustedValue).toString());
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onCancel}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "95%",
            maxWidth: "1200px",
            maxHeight: "95vh",
            borderRadius: "12px",
            minHeight: "600px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          p: 3,
          textAlign: "center",
          fontSize: "2rem",
          fontWeight: 600,
        }}
      >
        {title}
        {scheduledHeatName ? ` - ${scheduledHeatName}` : ""}
      </DialogTitle>

      <DialogContent sx={{ p: 4, minHeight: "400px" }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mt: 3 }}>
              <WeightInputForOperation
                value={inputValue}
                onChange={handleInputChange}
                disabled={false}
                bgColor="white"
                borderColor="primary.main"
                label={inputLabel}
                placeholder=""
              />

              <Box
                sx={{
                  mt: 4,
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 3,
                }}
              >
                {QUICK_ADJUSTMENTS.map(({ value, color }) => (
                  <Button
                    key={value}
                    variant="contained"
                    onClick={() => handleAdjustment(value)}
                    sx={{
                      minHeight: 90,
                      borderRadius: "8px",
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      bgcolor: color,
                      color: "white",
                      "&:hover": {
                        bgcolor: color,
                        filter: "brightness(0.92)",
                      },
                    }}
                  >
                    {value > 0 ? "+" : "-"}
                    {Math.abs(value).toLocaleString("en-US")}
                  </Button>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mt: 3 }}>
              <CustomKeypad
                value={inputValue}
                onChange={handleInputChange}
                layout={{
                  default: ["7 8 9", "4 5 6", "1 2 3", "{bksp} 0 {clear}"],
                }}
                isDecimal={false}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: "24px 32px 32px", width: "100%" }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              onClick={onCancel}
              disabled={isSaving}
              sx={{
                p: "16px 32px",
                borderRadius: "8px",
                fontSize: "1.4rem",
                fontWeight: 600,
                minHeight: "60px",
                width: "100%",
                bgcolor: (theme) => theme.palette.charges.status.inactive,
                color: "white",
              }}
            >
              CANCEL
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              onClick={() => onSave(numericValue)}
              disabled={isSaving || !isValid}
              sx={{
                p: "16px 32px",
                borderRadius: "8px",
                fontSize: "1.4rem",
                fontWeight: 600,
                minHeight: "60px",
                width: "100%",
                bgcolor: "primary.main",
                color: "white",
              }}
            >
              {isSaving ? (
                <CircularProgress size={32} color="inherit" />
              ) : (
                "SAVE"
              )}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
